import { Grade } from '@/types/game-types';
import type { ParticlePresetType } from '@/effects/particle-presets';

export type ComboTierId = string;

export interface ComboEffect {
  effectType: ParticlePresetType;
  intensity: number;
  duration: number;
  soundEffect?: string;
  screenShake?: boolean;
  slowMotion?: boolean;
}

export interface ComboTierConfig {
  id: ComboTierId;
  grade: Grade;
  minCount: number;
  effect: ComboEffect;
  labelKey?: string;
  displayColor?: string;
}

export class ComboRegistry {
  private tiers: ComboTierConfig[] = [];
  private readonly tierMap = new Map<ComboTierId, ComboTierConfig>();

  constructor(initialTiers: ComboTierConfig[] = []) {
    if (initialTiers.length > 0) {
      this.setTiers(initialTiers);
    }
  }

  /**
   * 以批次方式註冊多個連擊等級，方便擴展套件一次性注入設定。
   *
   * @param tiers 要加入的等級清單
   * @param options.replaceExisting 是否覆寫同 id 的既有設定
   */
  registerTierBatch(tiers: ComboTierConfig[], options: { replaceExisting?: boolean } = {}): void {
    const { replaceExisting = true } = options;
    tiers.forEach(tier => {
      if (!replaceExisting && this.tierMap.has(tier.id)) {
        return;
      }
      this.tierMap.set(tier.id, tier);
    });
    this.tiers = Array.from(this.tierMap.values()).sort((a, b) => a.minCount - b.minCount);
  }

  setTiers(tiers: ComboTierConfig[]): void {
    this.tierMap.clear();
    tiers.forEach(tier => this.tierMap.set(tier.id, tier));
    this.tiers = Array.from(this.tierMap.values()).sort((a, b) => a.minCount - b.minCount);
  }

  registerTier(tier: ComboTierConfig): void {
    this.tierMap.set(tier.id, tier);
    this.tiers = Array.from(this.tierMap.values()).sort((a, b) => a.minCount - b.minCount);
  }

  removeTier(id: ComboTierId): void {
    this.tierMap.delete(id);
    this.tiers = Array.from(this.tierMap.values()).sort((a, b) => a.minCount - b.minCount);
  }

  getTierById(id: ComboTierId): ComboTierConfig | undefined {
    return this.tierMap.get(id);
  }

  getTierForCount(count: number): ComboTierConfig {
    if (this.tiers.length === 0) {
      throw new Error('No combo tiers registered');
    }

    let candidate = this.tiers[0]!;
    for (const tier of this.tiers) {
      if (count >= tier.minCount) {
        candidate = tier;
      } else {
        break;
      }
    }

    return candidate;
  }

  getAllTiers(): ComboTierConfig[] {
    return [...this.tiers];
  }
}

export function createDefaultComboRegistry(): ComboRegistry {
  const registry = new ComboRegistry();

  registry.setTiers([
    {
      id: 'none',
      grade: Grade.C,
      minCount: 0,
      effect: { effectType: 'snowflake', intensity: 1.0, duration: 500 },
      labelKey: 'combo.none',
      displayColor: '#ffffff',
    },
    {
      id: 'double',
      grade: Grade.B,
      minCount: 2,
      effect: { effectType: 'magic', intensity: 1.5, duration: 800, soundEffect: 'combo_double' },
      labelKey: 'combo.double',
      displayColor: '#90ee90',
    },
    {
      id: 'triple',
      grade: Grade.A,
      minCount: 3,
      effect: {
        effectType: 'fire',
        intensity: 2.0,
        duration: 1200,
        soundEffect: 'combo_triple',
        screenShake: true,
      },
      labelKey: 'combo.triple',
      displayColor: '#ffa500',
    },
    {
      id: 'quadruple',
      grade: Grade.S,
      minCount: 4,
      effect: {
        effectType: 'explosion',
        intensity: 2.5,
        duration: 1500,
        soundEffect: 'combo_quadruple',
        screenShake: true,
      },
      labelKey: 'combo.quadruple',
      displayColor: '#ff6347',
    },
    {
      id: 'quintuple',
      grade: Grade.S,
      minCount: 5,
      effect: {
        effectType: 'lightning',
        intensity: 3.0,
        duration: 1800,
        soundEffect: 'combo_quintuple',
        screenShake: true,
      },
      labelKey: 'combo.quintuple',
      displayColor: '#ff1493',
    },
    {
      id: 'sextuple',
      grade: Grade.SS,
      minCount: 6,
      effect: {
        effectType: 'storm',
        intensity: 4.0,
        duration: 2200,
        soundEffect: 'combo_sextuple',
        screenShake: true,
        slowMotion: true,
      },
      labelKey: 'combo.sextuple',
      displayColor: '#9932cc',
    },
    {
      id: 'septuple',
      grade: Grade.SSS,
      minCount: 7,
      effect: {
        effectType: 'blackhole',
        intensity: 5.0,
        duration: 2500,
        soundEffect: 'combo_septuple',
        screenShake: true,
        slowMotion: true,
      },
      labelKey: 'combo.septuple',
      displayColor: '#ffd700',
    },
  ]);

  return registry;
}
